import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface PriceBreakdownByDosageProps {
  code: string;
  codeType: string;
}

/**
 * Detailed price breakdown showing all variations by dosage/unit and setting.
 * Shows the complete pricing matrix for a code with expandable details.
 */
const PriceBreakdownByDosage: React.FC<PriceBreakdownByDosageProps> = ({ code, codeType }) => {
  const { data: variations, isLoading } = useQuery({
    queryKey: ['price-breakdown', code, codeType],
    queryFn: async () => {
      let pricingTable = 'cpt_pricing_averages';
      let codeColumn = 'cpt_code';
      let avgColumn = 'avg_gross_charge';
      let medianColumn = 'median_gross_charge';
      let minColumn = 'min_gross_charge';
      let maxColumn = 'max_gross_charge';

      const codeTypeLower = codeType.toLowerCase();
      if (codeTypeLower === 'cpt') {
        pricingTable = 'cpt_pricing_averages';
        codeColumn = 'cpt_code';
      } else if (codeTypeLower === 'hcpcs') {
        pricingTable = 'hcpcs_pricing_averages';
        codeColumn = 'hcpcs_code';
      } else if (codeTypeLower === 'drg') {
        pricingTable = 'drg_pricing_averages';
        codeColumn = 'drg_code';
      } else if (codeTypeLower === 'ndc' || codeTypeLower === 'drugs') {
        pricingTable = 'drug_pricing_averages';
        codeColumn = 'drug_code';
        avgColumn = 'avg_charge';
        medianColumn = 'median_charge';
        minColumn = 'min_charge';
        maxColumn = 'max_charge';
      } else if (codeTypeLower === 'supply') {
        pricingTable = 'supply_pricing_averages';
        codeColumn = 'supply_code';
        avgColumn = 'avg_charge';
        medianColumn = 'median_charge';
        minColumn = 'min_charge';
        maxColumn = 'max_charge';
      } else if (codeTypeLower === 'rev' || codeTypeLower === 'revenue') {
        pricingTable = 'revenue_pricing_averages';
        codeColumn = 'revenue_code';
        avgColumn = 'avg_charge';
        medianColumn = 'median_charge';
        minColumn = 'min_charge';
        maxColumn = 'max_charge';
      }

      const { data, error } = await supabase
        .from(pricingTable)
        .select(`
          setting_type,
          normalized_unit,
          unit_quantity,
          ${avgColumn},
          ${medianColumn},
          ${minColumn},
          ${maxColumn},
          total_hospitals
        `)
        .eq(codeColumn, code);

      if (error) throw error;

      const SETTING_ORDER: Record<string, number> = { all_settings: 0, inpatient: 1, outpatient: 2, unspecified: 3 };
      return (data || [])
        .map(row => ({
          setting: row.setting_type || 'unspecified',
          unit: row.normalized_unit || 'unspecified',
          unitQty: Number(row.unit_quantity) || 1,
          avgPrice: row[avgColumn] || 0,
          medianPrice: row[medianColumn] || 0,
          minPrice: row[minColumn] || 0,
          maxPrice: row[maxColumn] || 0,
          hospitalCount: row.total_hospitals || 0,
          spread: ((row[maxColumn] - row[minColumn]) / row[minColumn] * 100) || 0
        }))
        // rows under 3 hospitals are statistical noise; hiding them also kills
        // most of the near-duplicate look on unit-messy codes
        .filter(v => v.hospitalCount >= 3)
        // group rows by billing unit so variants of the same unit sit together,
        // then all_settings -> inpatient -> outpatient within each group
        .sort((a, b) =>
          a.unit.localeCompare(b.unit) ||
          a.unitQty - b.unitQty ||
          (SETTING_ORDER[a.setting] ?? 9) - (SETTING_ORDER[b.setting] ?? 9) ||
          b.hospitalCount - a.hospitalCount);
    },
    refetchInterval: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="space-y-2 my-4">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  if (!variations || variations.length === 0) {
    return (
      <div className="border border-matrix-green/20 rounded-lg p-4 my-4 text-center bg-black/20">
        <p className="text-sm text-muted-foreground">
          No pricing variations available. We need data from at least 3 hospitals.
        </p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="my-6 border border-matrix-green/20 rounded-lg overflow-hidden bg-black/20">
        <div className="bg-black/60 px-4 py-3 border-b border-matrix-green/20">
          <h4 className="font-mono font-bold text-matrix-green text-sm">
            📊 Pricing Breakdown by Setting & Dosage
          </h4>
          <p className="text-xs text-muted-foreground mt-1">
            Different care settings and dosages can have vastly different prices
          </p>
        </div>

        <Table>
          <TableHeader>
            <TableRow className="border-matrix-green/20">
              <TableHead className="text-matrix-green font-mono text-xs">Care Setting</TableHead>
              <TableHead className="text-matrix-green font-mono text-xs">Dosage/Unit</TableHead>
              <TableHead className="text-matrix-green font-mono text-xs text-center">Hospitals</TableHead>
              <TableHead className="text-matrix-green font-mono text-xs text-right">Avg Price</TableHead>
              <TableHead className="text-matrix-green font-mono text-xs text-right">Median</TableHead>
              <TableHead className="text-matrix-green font-mono text-xs text-right">Min - Max</TableHead>
              <TableHead className="text-matrix-green font-mono text-xs text-right">Price Spread</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {variations.map((variation, idx) => (
              <TableRow key={idx} className="border-matrix-green/10 hover:bg-matrix-green/5">
                <TableCell className="text-xs">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Badge variant="outline" className="text-xs cursor-help">
                          {variation.setting === 'all_settings' ? '📊 All settings' :
                           variation.setting === 'inpatient' ? '🏥 Inpatient' :
                           variation.setting === 'outpatient' ? '🚶 Outpatient' :
                           '❓ Unspecified'}
                        </Badge>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {variation.setting === 'all_settings' ? (
                        <div className="text-xs">
                          <p className="font-semibold">All Settings Combined</p>
                          <p>Every listing for this code — inpatient, outpatient, and unlabeled — aggregated together.</p>
                        </div>
                      ) : variation.setting === 'inpatient' ? (
                        <div className="text-xs">
                          <p className="font-semibold">Inpatient Care</p>
                          <p>Hospital stay with overnight admission. Higher facility costs.</p>
                        </div>
                      ) : variation.setting === 'outpatient' ? (
                        <div className="text-xs">
                          <p className="font-semibold">Outpatient Care</p>
                          <p>Same-day service without overnight stay. Lower costs.</p>
                        </div>
                      ) : (
                        <div className="text-xs">
                          <p className="font-semibold">Unspecified</p>
                          <p>Hospital did not specify care setting.</p>
                        </div>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TableCell>
                <TableCell className="text-xs font-mono">
                  {variation.unit === 'unspecified' ? 'unlabeled line' :
                   variation.unit === 'other' ? 'per line (no unit given)' :
                   `per ${variation.unitQty > 1 ? `${variation.unitQty} ` : ''}${variation.unit}`}
                </TableCell>
                <TableCell className="text-center text-xs font-mono">
                  {variation.hospitalCount}
                </TableCell>
                <TableCell className="text-right text-xs font-mono font-semibold text-matrix-green">
                  ${variation.avgPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
                <TableCell className="text-right text-xs font-mono font-semibold text-blue-400">
                  ${variation.medianPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
                <TableCell className="text-right text-xs font-mono">
                  ${variation.minPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })} - ${variation.maxPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </TableCell>
                <TableCell className="text-right">
                  <Badge variant={variation.spread > 100 ? "destructive" : "secondary"} className="text-xs">
                    {variation.spread.toFixed(0)}%
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="bg-black/40 px-4 py-2 text-xs text-muted-foreground border-t border-matrix-green/20">
          💡 Price spread over 100% indicates potential price gouging. Same service, wildly different prices.
        </div>
      </div>
    </TooltipProvider>
  );
};

export default PriceBreakdownByDosage;
